import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { Bot, ChevronDown, X, Lock, CreditCard } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../utils/designSystem'
import { AVAILABLE_AGENTS } from '../agents'
import { useAuth } from '../hooks/useSupabaseAuth'
import { useSubscription } from '../hooks/useSubscription'
import { CREDIT_COSTS } from '../services/subscriptionService'

const AgentToggle = ({ selectedAgent, onAgentSelect, onAgentClear }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useAuth()
  const { hasActiveSubscription, creditsRemaining, subscription } = useSubscription()

  const handleAgentSelect = (agent) => {
    const status = getAgentStatus(agent)
    
    if (!user) {
      window.dispatchEvent(new CustomEvent('openAuthModal'))
      return
    }

    // Prevent selection if agent is not accessible
    if (!status.accessible) {
      // Show upgrade modal or message
      window.dispatchEvent(new CustomEvent('showUpgradeModal', {
        detail: {
          agentName: agent.name,
          requiredPackage: status.packageName === 'LARGE' ? 'PRO' : 'LARGE',
          message: `${agent.name} memerlukan paket ${status.packageName === 'LARGE' ? 'PRO' : 'LARGE atau PRO'} untuk dapat digunakan.`
        }
      }))
      return
    }

    // Prevent selection if no credits
    if (!status.hasCredits) {
      window.dispatchEvent(new CustomEvent('showCreditWarning', {
        detail: {
          message: 'Kredit Anda tidak mencukupi untuk menggunakan agent ini.'
        }
      }))
      return
    }

    onAgentSelect(agent)
    setIsOpen(false)
  }

  const getAgentStatus = (agent) => {
    if (!user) {
      return { accessible: false, hasCredits: false, needsLogin: true }
    }
    
    // Check if user has active subscription and sufficient credits
    const hasCredits = hasActiveSubscription && creditsRemaining > 0
    const packageName = subscription?.subscription_packages?.name
    const allowedAgents = subscription?.subscription_packages?.features?.allowed_agents || []
    
    // Define agent access requirements based on subscription packages
     const getAgentAccessRequirement = (agentId) => {
       switch (agentId) {
         case 'video_agent':
           return { requiredAgent: 'video', minPackages: ['LARGE', 'PRO'] }
         case 'skripsi_agent':
           return { requiredAgent: 'skripsi', minPackages: ['LARGE', 'PRO'] }
         case 'buku_agent':
           return { requiredAgent: 'buku', minPackages: ['PRO'] }
         case 'novel_agent':
           return { requiredAgent: 'novel', minPackages: ['PRO'] }
         default:
           return { requiredAgent: null, minPackages: [] }
       }
     }
    
    const accessReq = getAgentAccessRequirement(agent.id)
    
    // Check if agent requires specific package access
    if (accessReq.requiredAgent) {
      const hasAgentAccess = allowedAgents.includes(accessReq.requiredAgent)
      const hasRequiredPackage = accessReq.minPackages.includes(packageName)
      
      return {
        accessible: hasActiveSubscription && hasAgentAccess && hasRequiredPackage,
        hasCredits: hasCredits,
        needsLogin: false,
        packageName: packageName,
        needsUpgrade: !hasAgentAccess || !hasRequiredPackage
      }
    }
    
    // For agents without specific restrictions
    return {
      accessible: hasActiveSubscription,
      hasCredits: hasCredits,
      needsLogin: false
    }
  }

  const handleClearAgent = (e) => {
    e.stopPropagation()
    onAgentClear()
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center space-x-2 px-3 py-2 text-sm transition-all duration-300 ease-in-out rounded-lg min-w-0 transform hover:scale-105",
          selectedAgent 
            ? "text-white border border-white/20 hover:border-white/30" 
            : "text-gray-400 hover:text-white hover:bg-white/5 border border-white/10 hover:border-white/20"
        )}
        style={selectedAgent ? { backgroundColor: `${selectedAgent.color}15`, borderColor: `${selectedAgent.color}40` } : {}}
      >
        {selectedAgent ? (
          <>
            <selectedAgent.icon className="w-4 h-4 flex-shrink-0" style={{ color: selectedAgent.color }} />
            <span className="text-xs truncate" style={{ color: selectedAgent.color }}>{selectedAgent.name}</span>
            <div
              onClick={handleClearAgent}
              className="p-0.5 hover:bg-black/20 rounded transition-colors flex-shrink-0 cursor-pointer"
              style={{ color: selectedAgent.color }}
            >
              <X className="w-3 h-3" />
            </div>
          </>
        ) : (
          <>
            <Bot className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs">Pilih Agent</span>
            <ChevronDown className={cn('w-3 h-3 transition-transform flex-shrink-0', isOpen && 'rotate-180')} />
          </>
        )}
      </button>

      {/* Full Page Modal using Portal */}
      {isOpen && createPortal(
        <div className="fixed inset-0 bg-bg-primary z-[10000] overflow-y-auto animate-in fade-in-0 duration-300">
          {/* Close Button - Hidden on mobile (using sticky header instead) */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-6 right-6 p-2 text-text-secondary hover:text-text-primary transition-all duration-200 rounded-lg hover:bg-bg-secondary z-[10001] transform hover:scale-110 hidden md:block"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Sticky Header for Mobile */}
          <div className="sticky top-0 bg-bg-primary z-[10002] p-6 pb-4 border-b border-border-primary md:hidden">
            <div className="flex items-center justify-between">
              <div className="text-xl font-bold text-text-primary">Pilih AI Agent</div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-text-secondary hover:text-text-primary transition-all duration-200 rounded-lg hover:bg-bg-secondary transform hover:scale-110"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="min-h-screen p-6 pt-6 md:pt-20">
            <div className="max-w-7xl mx-auto">
              <div className="text-2xl font-bold text-text-primary mb-8 text-center animate-in slide-in-from-top-4 fade-in-0 duration-500 hidden md:block">Pilih AI Agent</div>
              
              {/* Responsive Grid: 1 column on mobile, 3 columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                   {AVAILABLE_AGENTS.map((agent, index) => {
                    const status = getAgentStatus(agent)
                    const isDisabled = !status.accessible || !status.hasCredits || status.needsLogin
                    
                    return (
                      <button
                         key={agent.id}
                         onClick={isDisabled ? undefined : () => handleAgentSelect(agent)}
                         disabled={isDisabled}
                         className={cn(
                           "group p-4 text-left transition-all duration-300 ease-in-out rounded-xl transform animate-in slide-in-from-bottom-4 fade-in-0",
                           isDisabled 
                             ? "opacity-60 cursor-not-allowed bg-bg-tertiary" 
                             : "hover:bg-bg-tertiary hover:shadow-lg bg-bg-primary hover:scale-[1.02]"
                         )}
                         style={{
                           animationDelay: `${index * 100}ms`,
                           animationDuration: '500ms'
                         }}
                       >
                        <div className="flex items-center space-x-3">
                           <div 
                             className={cn(
                               "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 relative shadow-sm transition-all duration-300 ease-in-out",
                               isDisabled ? "opacity-50" : ""
                             )}
                             style={{ backgroundColor: `${agent.color}15`, color: agent.color }}
                           >
                             <agent.icon className="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 ease-in-out group-hover:scale-110" />
                            {!status.accessible && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center shadow-sm">
                                <Lock className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                            {status.accessible && !status.hasCredits && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                                <CreditCard className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                             <div className={cn(
                               "font-semibold text-sm sm:text-base flex items-baseline gap-2 mb-1",
                               isDisabled ? "text-text-tertiary" : "text-text-primary"
                             )}>
                               {agent.name}
                               {user && (
                                  <span className="text-[10px] text-text-tertiary font-normal">
                                    {CREDIT_COSTS[`${agent.id}_agent`] || CREDIT_COSTS[agent.id] || '2'} kredit
                                  </span>
                                )}
                               {status.needsLogin && (
                                 <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full font-medium">
                                   Login
                                 </span>
                               )}
                               {!status.needsLogin && !status.accessible && (
                                 <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded-full font-medium">
                                   Pro
                                 </span>
                               )}
                             </div>
                             <div className="text-xs sm:text-sm text-text-tertiary line-clamp-2">
                               {agent.description}
                             </div>
                           </div>
                        </div>
                      </button>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

AgentToggle.propTypes = {
  selectedAgent: PropTypes.object,
  onAgentSelect: PropTypes.func.isRequired,
  onAgentClear: PropTypes.func.isRequired
}

export default AgentToggle